import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGoBack } from "@/hooks/use-go-back";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pencil, Search, Settings2, Trash2 } from "lucide-react";
import {
  deleteManagedEvent,
  getManagedEvent,
} from "@/features/supply-config/managed-events-api";
import { getEventTypesByManagedEvent } from "@/features/supply-config/event-types-api";
import {
  EVENT_TYPE_FLAGS,
  getEventTypeFlagsBatch,
  setManagedEventFlag,
  type EventTypeFlagType,
} from "@/features/supply-config/feature-flags-api";
import { ConfigureManagedEventDialog } from "@/features/supply-config/ui/configure-managed-event-dialog";
import { type DescriptionSection } from "@/features/supply-config/ui/scheduling-description-field";
import { ContentLayout } from "@/components/common/content-layout";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import { codeToFlag } from "@/features/regions/utils/code-to-flag";

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <div className="text-sm text-gray-900 mt-0.5">{value || "—"}</div>
    </div>
  );
}

export default function ManagedEventDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useGoBack("..");
  const qc = useQueryClient();
  const [configOpen, setConfigOpen] = useState(false);
  const [etSearch, setEtSearch] = useState("");

  const { data: me, isLoading, isFetching, error } = useQuery({
    queryKey: ["managed-event", id],
    queryFn: () => getManagedEvent(id!),
    enabled: !!id,
  });
  const { data: eventTypes = [] } = useQuery({
    queryKey: ["event-types", "by-managed", id],
    queryFn: () => getEventTypesByManagedEvent(id!),
    enabled: !!id,
  });
  const schedulingSections =
    (me?.metadata?.schedulingDescription as DescriptionSection[] | undefined) ??
    [];
  const schedulingTitle = (me?.metadata?.schedulingTitle as string) ?? "";
  const roundRobin = eventTypes.filter((e) => e.teams?.length);
  const individual = eventTypes.filter((e) => e.professional_id);
  const filteredIndividual = etSearch.trim()
    ? individual.filter((e) =>
        (e.professional?.name ?? "")
          .toLowerCase()
          .includes(etSearch.trim().toLowerCase()),
      )
    : individual;

  // Flags live per Calendly event-type uri, so the managed-event toggle reads
  // its state back from the children: all on / all off / mixed.
  const flagUris = eventTypes
    .map((e) => e.calendly_event_type_uri)
    .filter((uri): uri is string => !!uri);

  const { data: childFlags = [] } = useQuery({
    queryKey: ["feature-flags", "by-managed", id, flagUris],
    queryFn: () => getEventTypeFlagsBatch(flagUris),
    enabled: flagUris.length > 0,
  });

  const flagState = (type: EventTypeFlagType) => {
    const enabled = childFlags.filter(
      (f) => f.type === type && f.enabled,
    ).length;
    return {
      enabled,
      total: flagUris.length,
      // An event type with no row resolves to false, so "all on" means every
      // uri has an enabled row — not merely that no row says otherwise.
      allOn: flagUris.length > 0 && enabled === flagUris.length,
      mixed: enabled > 0 && enabled < flagUris.length,
    };
  };

  const usageAutomation = flagState("SESSION_USAGE_AUTOMATION");
  const autoCancellation = flagState("SESSION_AUTO_CANCELLATION");

  // How many event types have auto-cancellation on but usage automation off,
  // paired PER URI rather than compared group-wide.
  //
  // The webhook resolves both flags for the event type of the booking in hand
  // (isSessionUsageAutomationEnable(event_type) -> findFirst on that
  // identifier), so auto-cancellation on event type A is inert only when usage
  // automation is off for A. Asking whether usage automation is off for *any*
  // event type in the group would declare a working A inert because some
  // unrelated C is misconfigured — and the obvious response to that is to go
  // switch C on, changing an event type nobody meant to touch.
  const inertAutoCancelCount = (() => {
    const usageOnUris = new Set(
      childFlags
        .filter((f) => f.type === "SESSION_USAGE_AUTOMATION" && f.enabled)
        .map((f) => f.identifier),
    );
    return childFlags.filter(
      (f) =>
        f.type === "SESSION_AUTO_CANCELLATION" &&
        f.enabled &&
        !usageOnUris.has(f.identifier),
    ).length;
  })();

  const setGlobalFlag = useMutation({
    mutationFn: (vars: { type: EventTypeFlagType; enabled: boolean }) =>
      setManagedEventFlag(id!, vars),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["feature-flags"] });
      toast({
        title: res.enabled ? "Turned on" : "Turned off",
        description:
          `${res.updated} event type${res.updated === 1 ? "" : "s"} updated` +
          (res.skipped > 0
            ? ` · ${res.skipped} skipped (no Calendly link)`
            : ""),
      });
    },
    onError: (e: any) =>
      toast({
        title: "Error",
        description: e.response?.data?.message || "Failed",
        variant: "destructive",
      }),
  });

  const remove = useMutation({
    mutationFn: () => deleteManagedEvent(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["managed-events"] });
      toast({ title: "Managed event deleted" });
      navigate("..");
    },
    onError: (e: any) =>
      toast({
        title: "Error",
        description: e.response?.data?.message || "Failed",
        variant: "destructive",
      }),
  });

  return (
    <ContentLayout
      data={me ? [me] : []}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      contentClassName="space-y-6"
      errorMessage="Failed to load managed event"
      emptyState={{ message: "Managed event not found" }}
    >
      {me && (
        <div className="w-full space-y-6">
          <button
            type="button"
            onClick={goBack}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" /> Managed events
          </button>

          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold text-gray-900">{me.name}</h1>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline">{me.category}</Badge>
                {me.round_robin_pool_active && (
                  <Badge variant="outline">round-robin pool</Badge>
                )}
                <Badge
                  variant={me.status === "ACTIVE" ? "active" : "inactive"}
                  className="uppercase text-[10px]"
                >
                  {me.status}
                </Badge>
              </div>
            </div>
            <HasAccess permission="write">
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfigOpen(true)}
                >
                  <Settings2 className="h-4 w-4 mr-1" /> Configure
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("edit")}
                >
                  <Pencil className="h-4 w-4 mr-1" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    window.confirm(`Delete "${me.name}"?`) && remove.mutate()
                  }
                >
                  <Trash2 className="h-4 w-4 mr-1 text-red-500" /> Delete
                </Button>
              </div>
            </HasAccess>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Meta label="Category" value={me.category} />
                <Meta label="Attendees" value={me.participant_scope} />
                <Meta
                  label="Duration"
                  value={me.duration_minutes ? `${me.duration_minutes}m` : "—"}
                />
                <Meta
                  label="Round-robin pool"
                  value={me.round_robin_pool_active ? "Active" : "Off"}
                />
              </div>
              <Meta
                label={`Teams (${me.teams?.length ?? 0})`}
                value={
                  me.teams?.length ? (
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {me.teams.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => navigate(`../../teams/${t.id}`)}
                        >
                          <Badge variant="outline">
                            {t.region_rel
                              ? `${codeToFlag(t.region_rel.code)} `
                              : ""}
                            {t.name}
                          </Badge>
                        </button>
                      ))}
                    </div>
                  ) : (
                    "—"
                  )
                }
              />
            </CardContent>
          </Card>

          {/* Scheduling description (shown on the patient booking screen) */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                Scheduling description
              </CardTitle>
            </CardHeader>
            <CardContent>
              {schedulingTitle && (
                <div className="mb-4">
                  <p className="text-[11px] uppercase tracking-wide text-gray-400">
                    Title
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {schedulingTitle}
                  </p>
                </div>
              )}
              {schedulingSections.length === 0 ? (
                <p className="text-sm text-gray-400">
                  No scheduling description added — use Edit to add sections.
                </p>
              ) : (
                <div className="space-y-4">
                  {schedulingSections.map((section, i) => {
                    const bullets = (section.content ?? []).filter(Boolean);
                    return (
                      <div key={i} className="space-y-1">
                        {section.subTitle && (
                          <p className="text-sm font-medium text-gray-900">
                            {section.subTitle}
                          </p>
                        )}
                        {bullets.length > 0 && (
                          <ul className="list-disc pl-5 space-y-0.5">
                            {bullets.map((line, li) => (
                              <li key={li} className="text-sm text-gray-700">
                                {line}
                              </li>
                            ))}
                          </ul>
                        )}
                        {section.additionalConclusion && (
                          <p className="text-sm italic text-gray-500">
                            {section.additionalConclusion}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Automation — one switch per flag, applied to every event type
              under this managed event. */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Automation</CardTitle>
            </CardHeader>
            <CardContent>
              {flagUris.length === 0 ? (
                <p className="text-sm text-gray-400">
                  No event types linked to Calendly yet — nothing to automate.
                </p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {EVENT_TYPE_FLAGS.map((flag) => {
                    const state =
                      flag.type === "SESSION_USAGE_AUTOMATION"
                        ? usageAutomation
                        : autoCancellation;
                    // Every scheduleCancellation call in the Calendly webhook
                    // sits inside `if (isSessionUsageAutomationEnable)`, so
                    // auto-cancellation without usage automation is inert. Say
                    // so rather than showing a state that does nothing.
                    const inert =
                      flag.type === "SESSION_AUTO_CANCELLATION" &&
                      inertAutoCancelCount > 0;

                    return (
                      <div
                        key={flag.type}
                        className="flex items-start justify-between gap-6 py-3 first:pt-0 last:pb-0"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900">
                              {flag.label}
                            </p>
                            {state.mixed && (
                              <Badge variant="outline" className="text-xs">
                                {state.enabled} of {state.total}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {flag.description}
                          </p>
                          {inert && (
                            <p className="text-xs text-amber-600 mt-1">
                              Has no effect on {inertAutoCancelCount} of{" "}
                              {state.total} event type
                              {state.total === 1 ? "" : "s"} — the booking
                              webhook only reaches auto-cancellation through
                              Usage automation, which is off for
                              {inertAutoCancelCount === 1 ? " that one" : " those"}.
                            </p>
                          )}
                        </div>
                        <HasAccess
                          permission="write"
                          fallback={
                            <Badge
                              variant={state.allOn ? "active" : "secondary"}
                              className="text-xs shrink-0"
                            >
                              {state.allOn
                                ? "On"
                                : state.mixed
                                  ? "Partial"
                                  : "Off"}
                            </Badge>
                          }
                        >
                          <Switch
                            className="shrink-0 mt-0.5"
                            checked={state.allOn}
                            disabled={setGlobalFlag.isPending}
                            aria-label={`${flag.label} for all event types`}
                            onCheckedChange={(enabled) =>
                              setGlobalFlag.mutate({
                                type: flag.type,
                                enabled,
                              })
                            }
                          />
                        </HasAccess>
                      </div>
                    );
                  })}
                  <p className="pt-3 text-xs text-gray-400">
                    Applies to all {flagUris.length} linked event type
                    {flagUris.length === 1 ? "" : "s"}. Event types added later
                    do not inherit this — re-apply after Generate.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            {/* Round-robin (team) events */}
            <Card>
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-base">
                  Round-robin ({roundRobin.length})
                </CardTitle>
                {me.round_robin_pool_active && (
                  <HasAccess permission="write">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfigOpen(true)}
                    >
                      <Settings2 className="h-4 w-4 mr-1" /> Configure
                    </Button>
                  </HasAccess>
                )}
              </CardHeader>
              <CardContent>
                {roundRobin.length === 0 ? (
                  <p className="text-sm text-gray-400">
                    {me.round_robin_pool_active
                      ? "No round-robin events yet — use Configure."
                      : "Round-robin pool is off for this managed event."}
                  </p>
                ) : (
                  <div className="border border-gray-200 rounded-md divide-y">
                    {roundRobin.map((e) => (
                      <div
                        key={e.id}
                        className="flex items-center justify-between gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                        onClick={() => navigate(`../../event-types/${e.id}`)}
                      >
                        <Badge variant="outline">
                          👥 {(e.teams ?? []).map((t) => t.name).join(", ")}
                          {e.type ? ` · ${e.type}` : e.language ? ` · ${e.language}` : ""}
                        </Badge>
                        {!e.is_active && (
                          <Badge variant="inactive" className="shrink-0">
                            inactive
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Individual (per-professional) events */}
            <Card>
              <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
                <CardTitle className="text-base">
                  Individual ({individual.length})
                </CardTitle>
                <div className="flex items-center gap-2">
                  {individual.length > 8 && (
                    <div className="relative w-36">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <Input
                        value={etSearch}
                        onChange={(e) => setEtSearch(e.target.value)}
                        placeholder="Filter…"
                        className="h-8 pl-7 text-sm"
                      />
                    </div>
                  )}
                  <HasAccess permission="write">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfigOpen(true)}
                    >
                      <Settings2 className="h-4 w-4 mr-1" /> Configure
                    </Button>
                  </HasAccess>
                </div>
              </CardHeader>
              <CardContent>
                {individual.length === 0 ? (
                  <p className="text-sm text-gray-400">
                    No individual event types yet — use Configure → Generate.
                  </p>
                ) : (
                  <div className="border border-gray-200 rounded-md divide-y max-h-80 overflow-y-auto">
                    {filteredIndividual.map((e) => (
                      <div
                        key={e.id}
                        className="flex items-center justify-between gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                        onClick={() => navigate(`../../event-types/${e.id}`)}
                      >
                        <span className="truncate">
                          {e.professional?.name ?? "—"}
                        </span>
                        {!e.is_active && (
                          <Badge variant="inactive" className="shrink-0">
                            inactive
                          </Badge>
                        )}
                      </div>
                    ))}
                    {filteredIndividual.length === 0 && (
                      <p className="px-3 py-3 text-sm text-gray-400">
                        No matches.
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      <ConfigureManagedEventDialog
        managedEvent={me ?? null}
        open={configOpen}
        onOpenChange={setConfigOpen}
      />
    </ContentLayout>
  );
}
